import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import request from 'utils/request';
import {
  formatUsd, getBrowserTimeZone, getDefaultRange, getPresetRange, TRANSLATION_RANGE_PRESETS,
  validateRange,
} from 'utils/translationUsage';
import './style.scss';

const basicUserSelector = makeSelectBasicUser();
const count = (value) => typeof value === 'number' ? value.toLocaleString('en-US') : '—';
const MODEL_OPTIONS = [
  {key: 'gemini:gemini-flash', label: 'Gemini Flash'},
  {key: 'deepseek:deepseek-flash', label: 'DeepSeek Flash'},
  {key: 'google_nmt:general/nmt', label: 'Google NMT'},
  {key: 'openai_luna:gpt-luna', label: 'OpenAI Luna'},
];
const JOB_STATES = [
  ['queued', 'Queued'], ['running', 'Running'], ['delayed', 'Waiting to retry'],
  ['budgetBlocked', 'Budget blocked'], ['failed', 'Failed'],
  ['outcomeUnknown', 'Outcome unknown'], ['succeeded', 'Succeeded'],
  ['stale', 'Stale'], ['superseded', 'Superseded'],
];

const Metric = ({label, value, detail}) => (
  <div className="translation-status__metric">
    <span>{label}</span><strong>{value}</strong><small>{detail}</small>
  </div>
);

const ModelTable = ({models}) => (
  <table className="translation-status__table">
    <caption className="sr-only">Translation usage by model family</caption>
    <thead><tr>
      <th scope="col">Model</th><th scope="col">English articles</th>
      <th scope="col">Language outputs</th><th scope="col">Provider calls</th>
      <th scope="col">Settled cost (USD)</th><th scope="col">Uncertain / reserved (USD)</th>
    </tr></thead>
    <tbody>{models.map((model) => (
      <tr key={model.key}>
        <th scope="row">{model.label}<small>{count(model.jobs?.total)} jobs</small></th>
        <td data-label="English articles">{count(model.sourceArticles)}</td>
        <td data-label="Language outputs">{count(model.translations)}
          <small>{count(model.jobs?.succeeded)} successful jobs</small>
        </td>
        <td data-label="Provider calls">{count(model.calls?.total)}
          <small>{count(model.calls?.translation)} translation · {count(model.calls?.postEdit)} post-edit · {count(model.calls?.other)} other</small>
        </td>
        <td data-label="Settled cost (USD)">{formatUsd(model.cost?.actualMicros)}
          {model.cost?.unpricedCalls > 0 && <small>{count(model.cost.unpricedCalls)} calls without settled cost</small>}
        </td>
        <td data-label="Uncertain / reserved (USD)">{formatUsd(model.cost?.uncertainMaximumMicros)} / {formatUsd(model.cost?.reservedMicros)}</td>
      </tr>
    ))}</tbody>
  </table>
);

const TranslationStatus = () => {
  const {authToken} = useSelector(basicUserSelector);
  const timeZone = useMemo(() => getBrowserTimeZone(), []);
  const initialRange = useMemo(() => getDefaultRange(new Date(), timeZone), [timeZone]);
  const [range, setRange] = useState(initialRange);
  const [activeRange, setActiveRange] = useState(initialRange);
  const [selectedPreset, setSelectedPreset] = useState('last30Days');
  const [selectedModel, setSelectedModel] = useState('all');
  const [activeModel, setActiveModel] = useState('all');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const mounted = useRef(false);
  const version = useRef(0);
  const requestedRange = useRef(initialRange);
  const requestedPreset = useRef('last30Days');
  const requestedModel = useRef('all');
  const draftPreset = useRef('last30Days');
  const loadingRef = useRef(false);

  const load = useCallback(async (nextRange, preset = 'custom', model = requestedModel.current) => {
    if (!authToken) return;
    requestedRange.current = {...nextRange};
    requestedPreset.current = preset;
    requestedModel.current = model;
    version.current += 1;
    const {current} = version;
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const response = await request(
        `translations/admin/status?${new URLSearchParams({
          ...nextRange, timezone: timeZone, ...(model === 'all' ? {} : {model}),
        })}`, authToken
      );
      if (!mounted.current || current !== version.current) return;
      const data = response?.data || response;
      if (!data?.summary || !Array.isArray(data.models)) throw new Error('Translation status is unavailable.');
      setMetrics(data);
      setActiveRange(nextRange);
      setActiveModel(model);
    } catch (requestError) {
      if (mounted.current && current === version.current) {
        setError(requestError?.message || 'Translation status could not be loaded.');
      }
    } finally {
      if (mounted.current && current === version.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [authToken, timeZone]);

  const reload = useCallback((previousRange, preset, model = requestedModel.current) => {
    const nextRange = preset === 'custom' ? previousRange : getPresetRange(preset, new Date(), timeZone);
    if (draftPreset.current === preset && preset !== 'custom') setRange(nextRange);
    load(nextRange, preset, model);
  }, [load, timeZone]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; version.current += 1; };
  }, []);
  useEffect(() => { load(initialRange, 'last30Days'); }, [load, initialRange]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden && !loadingRef.current) reload(requestedRange.current, requestedPreset.current);
    }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, reload]);

  const apply = useCallback((event) => {
    event.preventDefault();
    const message = validateRange(range, new Date(), timeZone);
    setValidationError(message);
    if (!message) load(range, selectedPreset);
  }, [range, selectedPreset, load, timeZone]);
  const changeFrom = useCallback((event) => {
    const {value} = event.target;
    draftPreset.current = 'custom';
    setSelectedPreset('custom');
    setRange((previous) => ({...previous, from: value}));
  }, []);
  const changeTo = useCallback((event) => {
    const {value} = event.target;
    draftPreset.current = 'custom';
    setSelectedPreset('custom');
    setRange((previous) => ({...previous, to: value}));
  }, []);
  const selectPreset = useCallback((event) => {
    const preset = event.currentTarget.dataset.range;
    const nextRange = getPresetRange(preset, new Date(), timeZone);
    draftPreset.current = preset;
    setSelectedPreset(preset);
    setRange(nextRange);
    setValidationError('');
    load(nextRange, preset);
  }, [load, timeZone]);
  const selectModel = useCallback((event) => {
    const {value} = event.target;
    setSelectedModel(value);
    reload(requestedRange.current, requestedPreset.current, value);
  }, [reload]);
  const refresh = useCallback(() => reload(requestedRange.current, requestedPreset.current), [reload]);
  const retry = useCallback(() => reload(requestedRange.current, requestedPreset.current), [reload]);
  const toggleAutoRefresh = useCallback((event) => setAutoRefresh(event.target.checked), []);
  const summary = metrics?.summary;
  const updatedAt = metrics?.generatedAt && new Date(metrics.generatedAt);
  const updatedLabel = updatedAt && !Number.isNaN(updatedAt.valueOf())
    ? updatedAt.toLocaleString('en-GB', {timeZone, hour12: false}) : '—';
  const today = getDefaultRange(new Date(), timeZone).to;
  const rangeDirty = range.from !== activeRange.from || range.to !== activeRange.to;
  const modelOptions = [...new Map([...MODEL_OPTIONS, ...(metrics?.modelOptions || [])]
    .map((option) => [option.key, option])).values()];
  const activeModelLabel = modelOptions.find((option) => option.key === activeModel)?.label || 'All models';

  return (
    <section className="translation-status" aria-labelledby="translation-status-title" aria-busy={loading}>
      <div className="translation-status__heading">
        <div><h1 id="translation-status-title">Translation Status</h1>
          <p>Article translations, provider calls and usage costs.</p>
        </div>
        <span className="translation-status__timezone">Local time · {timeZone}</span>
      </div>
      <div className="translation-status__range-panel">
        <div className="translation-status__range-heading">
          <h2>Reporting period</h2>
          <span>Weeks start Monday · Rolling ranges include today</span>
        </div>
        <div className="translation-status__presets" role="group" aria-label="Quick date ranges">
          {TRANSLATION_RANGE_PRESETS.map(({id, label}) => (
            <button key={id} type="button" data-range={id} aria-pressed={selectedPreset === id} onClick={selectPreset}>
              {label}
            </button>
          ))}
        </div>
        <form className="translation-status__filters" onSubmit={apply} aria-label="Translation date range">
          <label><span>From</span><input type="date" value={range.from} max={today} onChange={changeFrom} /></label>
          <label><span>To</span><input type="date" value={range.to} max={today} onChange={changeTo} /></label>
          <label><span>AI model</span><select value={selectedModel} onChange={selectModel}>
            <option value="all">All models</option>
            {modelOptions.map(({key, label}) => <option key={key} value={key}>{label}</option>)}
          </select></label>
          <button type="submit">Apply</button>
          <button type="button" className="translation-status__secondary" disabled={loading} onClick={refresh}>Refresh</button>
          {selectedPreset === 'custom' && <span className="translation-status__custom-label">{rangeDirty ? 'Custom range · Apply to update' : 'Custom range'}</span>}
        </form>
        <div className="translation-status__refresh-line">
          <label><input type="checkbox" checked={autoRefresh} onChange={toggleAutoRefresh} /> Auto-refresh every 30 seconds</label>
          <span role="status">{loading ? 'Updating…' : `Updated ${updatedLabel}`}</span>
        </div>
      </div>
      {validationError && <p className="translation-status__message translation-status__message--error" role="alert">{validationError}</p>}
      {error && <div className="translation-status__message translation-status__message--error" role="alert">
        <span>{error}{metrics && ' Showing the last successfully loaded data.'}</span>
        <button type="button" onClick={retry}>Retry</button>
      </div>}
      {!metrics && loading && <p className="translation-status__message">Loading translation status…</p>}
      {metrics && <>
        <div className="translation-status__configuration">
          <div><span>Current model</span><strong>{metrics.configuration?.modelFamilyLabel || 'Not configured'}</strong>
            <small>{metrics.configuration?.model || 'No model selected'}</small></div>
          <span className={`translation-status__badge ${metrics.configuration?.enabled ? 'translation-status__badge--enabled' : ''}`}>
            Automatic translation {metrics.configuration?.enabled ? 'enabled' : 'disabled'}
          </span>
        </div>
        <div className="translation-status__metrics">
          <Metric label="Translated articles" value={count(summary.sourceArticles)} detail="Unique English source articles" />
          <Metric label="Language outputs" value={count(summary.translations)} detail="Unique source article + target language" />
          <Metric label="Provider calls" value={count(summary.calls?.total)} detail={`${count(summary.calls?.succeeded)} succeeded · ${count(summary.calls?.inFlight)} in flight · ${count(summary.calls?.outcomeUnknown)} uncertain`} />
          <Metric label="Settled usage cost" value={formatUsd(summary.cost?.actualMicros)} detail="USD · recorded usage and configured prices" />
        </div>
        <section className="translation-status__panel" aria-labelledby="translation-status-jobs">
          <div className="translation-status__section-heading"><h2 id="translation-status-jobs">Job status</h2><span>{count(summary.jobs?.total)} total jobs</span></div>
          <dl className="translation-status__states">{JOB_STATES.map(([key, label]) => (
            <div key={key} className={summary.jobs?.[key] > 0 ? `translation-status__state--${key}` : ''}>
              <dt>{label}</dt><dd>{count(summary.jobs?.[key])}</dd>
            </div>
          ))}</dl>
        </section>
        <section className="translation-status__panel" aria-labelledby="translation-status-models">
          <div className="translation-status__section-heading"><h2 id="translation-status-models">Usage by model</h2><span>{activeModelLabel} · {activeRange.from} — {activeRange.to}</span></div>
          <p className="translation-status__note">Model versions are combined by family. Gemini Flash and Gemini Pro remain separate.</p>
          {summary.jobs?.total === 0 && <p className="translation-status__message">{activeModel === 'all'
            ? 'No translation jobs were created in this period.' : 'No translation jobs match this model and period.'}</p>}
          {metrics.models.length > 0 && <ModelTable models={metrics.models} />}
        </section>
        <section className="translation-status__panel translation-status__costs" aria-labelledby="translation-status-costs">
          <div><h2 id="translation-status-costs">Cost reconciliation</h2>
            <p className="translation-status__note">Uncertain maximums and reservations are excluded from settled cost. These are usage estimates, not a provider invoice.</p></div>
          <dl>
            <div><dt>Uncertain maximum</dt><dd>{formatUsd(summary.cost?.uncertainMaximumMicros)}</dd></div>
            <div><dt>Reserved for calls</dt><dd>{formatUsd(summary.cost?.reservedMicros)}</dd></div>
            <div><dt>Calls without settled cost</dt><dd>{count(summary.cost?.unpricedCalls)}</dd></div>
          </dl>
        </section>
        <p className="translation-status__note translation-status__footnote">
          Includes automatic translation jobs created in the selected local dates (up to 180 days), with their current status and all accumulated calls and costs, including later retries.
          Calls include translation, post-editing, review and repair; uncertain dispatches are included. Successful jobs can exceed language outputs after repeat translations.
          Articles and language outputs are deduplicated across models in the totals, so model rows may not add up. Historical comparison experiments are excluded.
        </p>
      </>}
    </section>
  );
};

export default TranslationStatus;
